import { useContext, useMemo } from 'react';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useNavigateToArtwork from '@/hooks/useNavigateToArtwork';
import useTranslations from '@/i18n/useTranslations';
import ProfileContentLoader from '@/shared-components/loader/special-loaders/ProfileContentLoader';
import Text from '@/shared-components/text/components/Text';

import MetaDescripProfilePages from '../MetaDescripProfilePages';

import VirtualList from './components/VirtualList';

import './index.css';

function resolveArtworkSlug(artwork) {
  if (!artwork) {
    return null;
  }

  return (
    artwork.slug
    ?? artwork.product_slug
    ?? artwork.artwork_slug
    ?? artwork.artwork?.slug
    ?? artwork.product?.slug
    ?? artwork.artwork_data?.slug
    ?? artwork.product_data?.slug
    ?? artwork.product_id
    ?? null
  );
}

function resolveCollectionArtworkIdentity(artwork) {
  if (!artwork || typeof artwork !== 'object') {
    return null;
  }

  const holdingId = artwork?.holding_id ?? artwork?.product_id ?? artwork?.productId ?? null;
  const ownerCandidate = artwork?.owner ?? artwork?.product_owner ?? null;
  let ownerId = null;
  if (typeof ownerCandidate === 'string' || typeof ownerCandidate === 'number') {
    ownerId = `${ownerCandidate}`;
  } else if (ownerCandidate && typeof ownerCandidate === 'object') {
    ownerId = ownerCandidate.id ?? ownerCandidate._id ?? ownerCandidate.owner_id ?? ownerCandidate.ownerId ?? null;
    ownerId = ownerId ? `${ownerId}` : null;
  }
  const holdingType = artwork?.metadata?.holding_type ?? artwork?.product_metadata?.holding_type ?? artwork?.holding_type ?? null;
  const slug = resolveArtworkSlug(artwork);
  const artworkId = artwork?._id ?? artwork?.id ?? slug ?? null;

  if (holdingType === 'limited_edition') {
    const edition = artwork?.product_metadata?.limited_edition ?? artwork?.metadata?.limited_edition ?? {};
    const editionNumber = edition?.number_value ?? edition?.number ?? 'no-number';
    const editionSize = edition?.size_key ?? edition?.size ?? 'no-size';
    return ['limited-edition', `${artworkId ?? 'unknown'}`, ownerId ?? 'no-owner', `${holdingId ?? 'no-holding'}`, `${editionNumber}`, `${editionSize}`].join('::');
  }

  if (artworkId && holdingType === 'original') {
    return ['original', `${artworkId}`, ownerId ?? 'no-owner'].join('::');
  }

  if (holdingId) {
    return ['holding', slug ?? 'unknown', `${holdingId}`, ownerId ?? 'no-owner', holdingType ?? 'no-type'].join('::');
  }

  return artworkId ? ['artwork', `${artworkId}`].join('::') : null;
}

function dedupeCollectionArtworks(artworks = []) {
  if (!Array.isArray(artworks) || artworks.length === 0) {
    return [];
  }

  const seen = new Set();
  return artworks.filter((artwork, index) => {
    const identity = resolveCollectionArtworkIdentity(artwork) ?? `fallback::${index}`;
    if (seen.has(identity)) {
      return false;
    }
    seen.add(identity);
    return true;
  });
}

export default function ProfileCollection() {
  const { profileData, internal } = useContext(ProfileDataContext);
  const t = useTranslations('common.profile.pages.collection');
  const profileTitle = profileData?.user_profile_name ?? profileData?.user_name ?? profileData?.user_username ?? '';
  const visibilityFilter = internal ? 'all' : 'public';

  const { loading, fetchData, loadMoreData } = useCache(
    `${profileData._id}_collection`,
    `${config.apis.api.url}/collection/${profileData._id}`,
    {
      injectToken: true,
      includeSearchQueries: { visibility: visibilityFilter },
      invalidateWhen: internal
        ? ["BUY_ARTWORK", "EDIT_COLLECTION", `${profileData._id}_REFRESH`]
        : [`${profileData._id}_REFRESH`],
    }
  );

  const sanitizedCollectionData = useMemo(() => {
    if (!fetchData || !Array.isArray(fetchData.data)) {
      return fetchData;
    }

    let mutated = false;
    const nextData = fetchData.data.map((artwork) => {
      const slug = resolveArtworkSlug(artwork);
      if (!slug || artwork.slug === slug) {
        return artwork;
      }

      mutated = true;
      return {
        ...artwork,
        slug,
      };
    });

    if (!mutated) {
      return fetchData;
    }

    return {
      ...fetchData,
      data: nextData,
    };
  }, [fetchData]);

  const navigateToArtwork = useNavigateToArtwork(
    `${profileData._id}_collection`,
    sanitizedCollectionData,
    `/collection/${profileData._id}?`,
    { type: 'profile-navigation', label: profileTitle }
  );

  const artworks = sanitizedCollectionData && Array.isArray(sanitizedCollectionData.data)
    ? sanitizedCollectionData.data
    : [];

  const publicArtworks = useMemo(
    () => artworks.filter((artwork) => !(artwork?.product_metadata?.is_private)),
    [artworks],
  );

  const dedupedPublicArtworks = useMemo(
    () => dedupeCollectionArtworks(publicArtworks),
    [publicArtworks],
  );
  const publicCollectionCount = Number(sanitizedCollectionData?.totalDocs) || dedupedPublicArtworks.length;
  const claimableArtworksCount = useMemo(() => {
    const rawCount =
      profileData?.user_profile_info?.claimable_artworks ??
      profileData?.user_profile_info?.claimable_artworks_count ??
      0;

    return Number.isFinite(rawCount) ? rawCount : 0;
  }, [profileData?.user_profile_info?.claimable_artworks, profileData?.user_profile_info?.claimable_artworks_count]);

  return (
    <>
      <MetaDescripProfilePages
        fetchData={profileData}
        subPage="collection"
        internal={internal}
        itemCount={publicCollectionCount}
      />
      {loading ? (
        <div className="profile-collection-loader-container">
          <ProfileContentLoader />
        </div>
      ) : dedupedPublicArtworks.length === 0 ? (
        <Text className="user-profile__collection-empty-view font-size-medium" paragraph>
          {internal
            ? claimableArtworksCount > 0
              ? t('empty.internal_claimable', { count: claimableArtworksCount })
              : t('empty.internal_default')
            : t('empty.external_private')}
        </Text>
      ) : (
        <VirtualList
          items={sanitizedCollectionData ? { ...sanitizedCollectionData, data: dedupedPublicArtworks } : { data: dedupedPublicArtworks, totalDocs: dedupedPublicArtworks.length }}
          onLoadMore={loadMoreData}
          navigateToArtwork={navigateToArtwork}
          profileCollection={true}
        />
      )}
    </>
  );
}
